import { spokenText } from '../utils/spokenText';

/**
 * Browser-native speech I/O for the hybrid voice interview.
 *
 *   • STT  = webkitSpeechRecognition (Chrome/Edge) — used ONLY for live captions + robust
 *            endpointing / turn-taking. Google's engine ignores background noise and detects
 *            end-of-speech far better than a hand-rolled VAD. The *accurate* transcript that the
 *            engine sees still comes from configured server STT (see transcribeVoiceTurn).
 *   • TTS  = SpeechSynthesis — natural built-in voices, no server audio streaming.
 *
 * Both degrade gracefully: if STT is unsupported the caller falls back to typing; if TTS is
 * unsupported the question is shown as text.
 */

export const ttsSupported = typeof window !== 'undefined' && 'speechSynthesis' in window;

function recognitionCtor() {
  if (typeof window === 'undefined') return null;
  return window.SpeechRecognition || window.webkitSpeechRecognition || null;
}

export const sttSupported = typeof window !== 'undefined' && recognitionCtor() !== null;

// Prefer a natural-sounding English voice over the robotic default.
const PREFERRED = [
  'Google US English', 'Microsoft Aria', 'Microsoft Jenny', 'Microsoft Michelle',
  'Samantha', 'Microsoft Zira',
];

let cachedVoice = null;
function pickVoice() {
  if (!ttsSupported) return null;
  if (cachedVoice) return cachedVoice;
  const voices = window.speechSynthesis.getVoices();
  if (!voices.length) return null;
  for (const name of PREFERRED) {
    const v = voices.find((x) => x.name && x.name.includes(name));
    if (v) { cachedVoice = v; return v; }
  }
  cachedVoice = voices.find((x) => x.lang && x.lang.startsWith('en')) || voices[0];
  return cachedVoice;
}

if (ttsSupported) {
  // Voices load asynchronously in some browsers.
  window.speechSynthesis.onvoiceschanged = () => { cachedVoice = null; pickVoice(); };
}

/** Speak `text` aloud; resolves when finished (or immediately if unsupported). */
export function speak(text, { rate = 0.97, lang = 'en-US' } = {}) {
  text = spokenText(text);
  if (!ttsSupported || !text) return Promise.resolve();
  return new Promise((resolve) => {
    window.speechSynthesis.cancel();
    const u = new SpeechSynthesisUtterance(text);
    const v = pickVoice();
    if (v) u.voice = v;
    u.lang = lang;
    u.rate = rate;     // a touch slower reads as more natural/measured
    u.pitch = 1;
    u.onend = () => resolve();
    u.onerror = () => resolve();
    window.speechSynthesis.speak(u);
  });
}

export function stopSpeaking() {
  if (ttsSupported) window.speechSynthesis.cancel();
}

/**
 * Build a speech recognizer. `onTranscript(text, isFinal)` gets the running (interim+final) text;
 * `onEnd()` fires when the engine stops (Chrome auto-stops after a silence — caller restarts).
 * Browser STT errors are surfaced separately for diagnostics, then routed through onEnd so the
 * existing restart/finalize flow still runs.
 */
export function createRecognizer(onTranscript, onEnd, lang = 'en-US', onError = null) {
  const Ctor = recognitionCtor();
  if (!Ctor) return null;
  const rec = new Ctor();
  rec.lang = lang;
  rec.continuous = true;
  rec.interimResults = true;
  let seedText = '';
  let finalText = '';
  let runningText = '';
  const finalSegments = new Map();
  let ended = false;
  let endDetails = {};
  const waiters = new Set();
  let audioSource = 'unknown';
  const finish = (details) => {
    if (ended) return;
    ended = true;
    endDetails = details;
    waiters.forEach((done) => done());
    waiters.clear();
    if (onEnd) onEnd(details);
  };
  rec.onresult = (e) => {
    if (ended) return;
    const interim = [];
    for (let i = 0; i < e.results.length; i += 1) {
      const r = e.results[i];
      if (r.isFinal) finalSegments.set(i, r[0].transcript.trim());
      else interim.push(r[0].transcript.trim());
    }
    finalText = [seedText, ...[...finalSegments].sort((a, b) => a[0] - b[0]).map((item) => item[1])]
      .filter(Boolean).join(' ').trim();
    runningText = [finalText, ...interim].filter(Boolean).join(' ').trim();
    onTranscript(runningText, !interim.length && Boolean(finalSegments.size), finalText);
  };
  rec.onend = () => finish({ source: 'end' });
  rec.onerror = (event) => {
    if (ended) return;
    const details = {
      source: 'error',
      error: event && event.error ? event.error : 'unknown',
      message: event && event.message ? event.message : '',
      type: event && event.type ? event.type : 'error',
    };
    if (onError) onError(details);
    finish(details);
  };
  rec.onnomatch = () => {
    if (ended) return;
    const details = { source: 'nomatch', error: 'nomatch', message: '', type: 'nomatch' };
    if (onError) onError(details);
  };
  return {
    // `seed` carries over text from a recognizer that Chrome auto-stopped mid-answer, so resuming keeps
    // the words already spoken instead of starting the caption from scratch.
    start(seed = '', audioTrack = null) {
      seedText = seed.trim();
      finalText = seedText;
      runningText = seedText;
      try {
        if (audioTrack && audioTrack.readyState === 'live') {
          try { rec.start(audioTrack); audioSource = 'captured_track_requested'; }
          catch (error) {
            if (!['TypeError', 'NotSupportedError', 'InvalidStateError'].includes(error?.name)) throw error;
            rec.start(); audioSource = 'default_microphone';
          }
        } else { rec.start(); audioSource = 'default_microphone'; }
        return true;
      } catch (error) {
        const details = {
          source: 'start',
          error: 'start_failed',
          message: error && error.message ? error.message : String(error || ''),
          type: error && error.name ? error.name : 'start_failed',
        };
        if (onError) onError(details);
        finish(details);
        return false;
      }
    },
    getAudioSource() { return audioSource; },
    getSnapshot() { return { runningText, finalText, audioSource, ...endDetails }; },
    waitForFinal(timeoutMs = 1000) {
      if (ended) return Promise.resolve({ runningText, finalText, audioSource, ...endDetails });
      return new Promise((resolve) => {
        const done = () => {
          clearTimeout(timer);
          waiters.delete(done);
          resolve({ runningText, finalText, audioSource, ...endDetails });
        };
        const timer = setTimeout(done, timeoutMs);
        waiters.add(done);
      });
    },
    stop() { try { rec.stop(); } catch { /* already stopped */ } },
    abort() { try { rec.abort(); } catch { /* ignore */ } },
  };
}
